import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AuthContextApiError,
  fetchAuthContext,
  fetchAuthContextWithRetry,
  refreshAuthContext,
} from '@/uptk/core/api/auth_context_api';
import type { AuthContextResponse } from '@/types/auth_context';
import {
  clearAuthContextState,
  getAuthContextState,
  setAuthContextState,
} from '@/uptk/core/api/auth_context_store';
import { syncSaasContext, clearSaasContext } from '@/uptk/core/api/saasContext';
import { clearTenantOverrideState } from '@/uptk/core/api/tenant_override_store';

const TOKEN_REFRESH_POLL_MS = 60_000;

interface ImportMetaEnv {
  readonly VITE_CLERK_JWT_TEMPLATE?: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

const defaultJwtTemplate = import.meta.env.VITE_CLERK_JWT_TEMPLATE;

type RefreshOptions = {
  silent?: boolean;
  forceNetwork?: boolean;
  tokenOverride?: string;
  /** When true, retries on transient 5xx for the ACA cold-start window. */
  isBootstrap?: boolean;
};

const normalizeError = (error: unknown) => {
  if (error instanceof AuthContextApiError) {
    return error;
  }
  if (error instanceof Error) {
    return new AuthContextApiError(error.message, null, null);
  }
  return new AuthContextApiError('Failed to load authentication context.', null, null);
};

const buildTokenOptions = () => {
  if (!defaultJwtTemplate) {
    return undefined;
  }
  return { template: defaultJwtTemplate };
};

const isMissingTemplateError = (error: unknown) => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return message.includes('No JWT template exists with name:');
};

export const useAuthContext = () => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [context, setContext] = useState<AuthContextResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<AuthContextApiError | null>(null);
  const [warmingUp, setWarmingUp] = useState<boolean>(false);
  const tokenRef = useRef<string | null>(null);
  const refreshInFlightRef = useRef<boolean>(false);
  // Tracks a permanent (non-retryable) auth failure (e.g. 403 FORBIDDEN).
  // The polling interval skips refresh while this flag is set to prevent an
  // infinite loop when the JWT rotates but the user still has no tenant access.
  const permanentErrorRef = useRef<boolean>(false);

  const applyContext = useCallback((nextContext: AuthContextResponse) => {
    permanentErrorRef.current = false;
    // This callback also runs on the silent 60s JWT-rotation poll, so it must not
    // discard an active tenant override: clearing it unconditionally (and passing
    // the base tenant as an explicit option below, which outranks the override in
    // resolveSaasContext) reverted the operator's tenant selection back to the
    // session/owner tenant one poll after every switch. Drop the override only
    // when the signed-in user actually changes; sign-out and 401/403/404 already
    // clear it in refresh().
    const previousUserId = getAuthContextState()?.userId;
    if (previousUserId && previousUserId !== nextContext.userId) {
      clearTenantOverrideState();
    }
    setAuthContextState(nextContext);
    syncSaasContext({ authContext: nextContext });
    setContext(nextContext);
  }, []);

  const resolveToken = useCallback(async (): Promise<string | null> => {
    const preferredOptions = buildTokenOptions();
    try {
      const token = await getToken(preferredOptions);
      return token ?? null;
    } catch (error) {
      if (preferredOptions?.template && isMissingTemplateError(error)) {
        const fallbackToken = await getToken();
        return fallbackToken ?? null;
      }
      throw error;
    }
  }, [getToken]);

  const refresh = useCallback(
    async (options: RefreshOptions = {}) => {
      if (!isLoaded) {
        return null;
      }

      if (!isSignedIn) {
        permanentErrorRef.current = false;
        clearTenantOverrideState();
        clearAuthContextState();
        clearSaasContext();
        tokenRef.current = null;
        setContext(null);
        setError(null);
        setLoading(false);
        return null;
      }

      if (refreshInFlightRef.current) {
        return null;
      }

      refreshInFlightRef.current = true;
      if (!options.silent) {
        setLoading(true);
      }

      try {
        const token = options.tokenOverride ?? (await resolveToken());
        if (!token) {
          throw new AuthContextApiError('No authentication token available.', 401, 'UNAUTHORIZED');
        }

        let nextContext: AuthContextResponse;
        if (options.isBootstrap) {
          nextContext = await fetchAuthContextWithRetry(token, () => setWarmingUp(true));
        } else if (options.forceNetwork) {
          nextContext = await refreshAuthContext(token);
        } else {
          nextContext = await fetchAuthContext(token);
        }

        tokenRef.current = token;
        applyContext(nextContext);
        setError(null);
        setWarmingUp(false);
        return nextContext;
      } catch (refreshError) {
        const normalizedError = normalizeError(refreshError);
        const shouldClearContext =
          normalizedError.status === 401 ||
          normalizedError.status === 403 ||
          normalizedError.status === 404;
        if (shouldClearContext) {
          clearTenantOverrideState();
          clearAuthContextState();
          setContext(null);
          tokenRef.current = null;
        }
        if (normalizedError.status === 403) {
          // 403 is permanent for this session — clear stale tenant headers from
          // localStorage and axios defaults, and stop the polling interval from
          // retrying (which would loop every time the JWT rotates).
          permanentErrorRef.current = true;
          clearSaasContext();
        }
        setWarmingUp(false);
        setError(normalizedError);
        return null;
      } finally {
        refreshInFlightRef.current = false;
        if (!options.silent) {
          setLoading(false);
        }
      }
    },
    [applyContext, isLoaded, isSignedIn, resolveToken]
  );

  useEffect(() => {
    if (!isLoaded) {
      setLoading(true);
      return;
    }
    void refresh({ isBootstrap: true });
  }, [isLoaded, isSignedIn, refresh]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      return;
    }

    const interval = window.setInterval(async () => {
      // Never retry after a permanent (non-recoverable) auth failure such as
      // 403 FORBIDDEN. The UI shows a sign-out prompt; the user must act.
      if (permanentErrorRef.current) {
        return;
      }

      const token = await resolveToken();
      if (!token) {
        return;
      }

      if (tokenRef.current == null) {
        tokenRef.current = token;
        return;
      }

      if (tokenRef.current !== token) {
        await refresh({
          silent: true,
          forceNetwork: true,
          tokenOverride: token,
        });
      }
    }, TOKEN_REFRESH_POLL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [isLoaded, isSignedIn, refresh, resolveToken]);

  return {
    context,
    loading,
    error,
    warmingUp,
    refresh,
    isLoaded,
    isSignedIn,
  };
};
